import React, { useState, useRef } from "react";
import { Pressable, StyleSheet, View, Animated } from "react-native";
import { Circle, CircleCheck, EllipsisVertical, RotateCcw, Trash2, Sparkles, Clock } from "lucide-react";
import { Menu } from "react-native-paper";
import { colors, radii, spacing, shadowStyles } from "../../../styles/design-system";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import Pill from "../../common/Pill/Pill";
import { formatTimeAgo } from "../../../utils/date";

export interface Objective {
  id: string;
  objective: string;
  sequence?: number;
  status?: string;
}

export type ActivityStatus = "not_started" | "in_progress" | "completed";

export interface CompanionILECardProps {
  id: string;
  title: string;
  courseName?: string;
  description?: string;
  thumbnail?: string;
  created?: Date | string;
  objectives?: Objective[];
  status?: ActivityStatus;
  onPress?: () => void;
  onResetProgress?: () => void;
  onDeleteActivity?: () => void;
}

const CompanionILECard: React.FC<CompanionILECardProps> = ({
  title,
  courseName,
  created,
  objectives = [],
  status = "not_started",
  onPress,
  onResetProgress,
  onDeleteActivity,
}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false); 
  const scaleAnim = useRef(new Animated.Value(1)).current;

  const openMenu = () => setMenuVisible(true);
  const closeMenu = () => setMenuVisible(false);

  const handleHoverIn = () => {
    setIsHovered(true);
    Animated.spring(scaleAnim, {
      toValue: 1.03,
      useNativeDriver: true,
      speed: 20,
      bounciness: 4,
    }).start();
  };

  const handleHoverOut = () => {
    setIsHovered(false);
    Animated.spring(scaleAnim, {
      toValue: 1,
      useNativeDriver: true,
      speed: 20,
      bounciness: 4,
    }).start();
  };

  const handleResetProgress = () => {
    closeMenu();
    onResetProgress?.();
  };

  const handleDeleteActivity = () => {
    closeMenu();
    onDeleteActivity?.();
  };

  // Sort objectives by sequence and take first 3
  const sortedObjectives = [...objectives].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  const displayedObjectives = sortedObjectives.slice(0, 3);
  const remainingCount = objectives.length - displayedObjectives.length;

  // Check if objective is completed
  const isObjectiveCompleted = (objective: Objective) => {
    return objective.status === "completed";
  };

  // Get button text based on status
  const getButtonText = () => {
    switch (status) {
      case "completed":
        return "Review Activity";
      case "in_progress":
        return "Continue Learning";
      case "not_started":
      default:
        return "Start Learning";
    }
  };

  // show "New" if created within last 24 hours
  const isNew = (date: Date | string): boolean => {
    const d = typeof date === "string" ? new Date(date) : date;
    const hoursDiff = (Date.now() - d.getTime()) / (1000 * 60 * 60);
    return hoursDiff < 24;
  };

  const completedCount = objectives.filter(isObjectiveCompleted).length;

  // Compute progress percent based on objectives
  const displayProgress = objectives.length > 0 ? Math.round((completedCount / objectives.length) * 100) : 0;

  return (
    <Animated.View
      onPointerEnter={handleHoverIn}
      onPointerLeave={handleHoverOut}
      style={[
        styles.cardContainer,
        isHovered && styles.cardContainerHovered,
        { transform: [{ scale: scaleAnim }] },
      ]}>

      {/* Card Header: Icon + "Interactive Learning" pill + "New" pill + Menu */}
      <View style={styles.cardHeader}>
        <View style={styles.headerLeft}>
          <View style={styles.iconWrapper}>
            <Sparkles size={18} color="#7244bb" />
          </View>
          <View style={styles.interactivePill}>
            <CustomText
              text="Interactive Learning"
              size="xs"
              style={styles.interactivePillText}
              useTranslationText={false}
            />
          </View>
          <View >
            {created && isNew(created) &&
              <Pill
                text={"New"}
                backgroundColor={"#f3fee7"}
                textColor={"#3b7c0f"}
                textSize="xs"
                borderWidth={1}
              />}
          </View>
        </View>

        {/* Menu */}
        <Menu
          visible={menuVisible}
          onDismiss={closeMenu}
          anchor={
            <Pressable
              onPress={openMenu}
              style={styles.menuButton}
              accessibilityLabel="Open activity options menu"
              accessibilityRole="button">
              <View style={{ transform: [{ rotate: "90deg" }] }}>
                <EllipsisVertical size={16} color={colors.grey[400]} />
              </View>
            </Pressable>
          }
          anchorPosition="bottom"
          contentStyle={styles.menuContent}>
          <Menu.Item
            onPress={handleResetProgress}
            title="Reset Progress"
            leadingIcon={() => <RotateCcw size={14} color="#0C111D" />}
            titleStyle={{ color: "#0C111D", fontSize: 14 }}
          />
          <Menu.Item
            onPress={handleDeleteActivity}
            title="Delete Activity"
            leadingIcon={() => <Trash2 size={14} color="#DC2626" />}
            titleStyle={{ color: "#DC2626", fontSize: 14 }}
          />
        </Menu>
      </View>

      {/* Details Section */}
      <View style={styles.detailsContainer}>
        <View style={styles.detailsContent}>
          {/* Title */}
          <CustomText
            text={title}
            size="l"
            weight="bold"
            style={styles.title}
            useTranslationText={false}
            numberOfLines={2}
          />

          {/* Course Name */}
          {courseName && (
            <CustomText text={courseName} size="s" style={styles.courseName} useTranslationText={false} />
          )}

          {/* Suggested time */}
          {created && (
            <View style={styles.subtitleRow}>
              <Clock size={12} color={colors.grey[400]} style={styles.clockIcon} />
              <CustomText
                text={`Suggested ${formatTimeAgo(created)}`}
                size="xs"
                style={styles.subtitleText}
                useTranslationText={false}
              />
            </View>
          )}

          {/* Learning Objectives */}
          {objectives.length > 0 && (
            <View style={styles.learningGoalsContainer}>
              <View style={styles.learningGoalsHeader}>
                <CustomText
                  text="Learning Objectives"
                  size="xs"
                  style={styles.learningGoalsTitle}
                  useTranslationText={true}
                />
                <CustomText
                  text={`${completedCount}/${objectives.length}`}
                  size="xs"
                  style={styles.progressCount}
                  useTranslationText={false}
                />
              </View>

              {displayedObjectives.map((objective) => {
                const completed = isObjectiveCompleted(objective);
                return (
                  <View key={objective.id} style={styles.objectiveRow}>
                    {completed ? (
                      <CircleCheck size={16} color="#16A34A" style={styles.objectiveIcon} />
                    ) : (
                      <Circle size={16} color={colors.grey[400]} style={styles.objectiveIcon} />
                    )}
                    <CustomText
                      text={objective.objective}
                      size="xs"
                      style={[styles.objectiveText, completed && styles.completedObjectiveText]}
                      useTranslationText={false}
                      numberOfLines={1}
                    />
                  </View>
                );
              })}

              {remainingCount > 0 && (
                <CustomText
                  text={`+${remainingCount} more`}
                  size="xs"
                  style={styles.moreText}
                  useTranslationText={false}
                />
              )}
            </View>
          )}

          {/* Progress Bar (derived from objectives) */}
          {status === "in_progress" && objectives.length > 0 && (
            <View style={styles.progressSection}>
              <View style={styles.progressLabelRow}>
                <CustomText text="Progress" size="s" style={styles.progressLabel} useTranslationText={false} />
                <CustomText text={`${displayProgress}%`} size="s" style={styles.progressLabel} useTranslationText={false} />
              </View>
              <View style={styles.progressTrack}>
                <View style={[styles.progressFill, { width: `${displayProgress}%` as any }]} />
              </View>
            </View>
          )}

          {/* Completed status label */}
          {status === "completed" && (
            <View style={styles.completedRow}>
              <CircleCheck size={16} color="#3b7c0f" />
              <CustomText text="Completed" size="s" style={styles.completedLabel} useTranslationText={false} />
            </View>
          )}
        </View>

        {/* Action Button */}
        <CustomButton
          text={getButtonText()}
          onPress={onPress}
          styleType="lightStroke"
          size="s"
          style={styles.actionButton}
          textStyle={{ color: colors.grey[600], fontWeight: "400" }}
          useTranslationText={false}
        />
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    backgroundColor: colors.white,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: "#e9d5ff",
    overflow: "hidden",
    ...shadowStyles.sm,
    maxWidth: 400,
    flex: 1,
  },
  cardContainerHovered: {
    boxShadow: "rgba(0, 0, 0, 0.12) 0px 12px 32px",
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xs,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  iconWrapper: {
    width: 40,
    height: 40,
    borderRadius: radii.lg,
    backgroundColor: "#F3E8FF",
    alignItems: "center",
    justifyContent: "center",
  },
  interactivePill: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: radii.full,
    backgroundColor: "#F3E8FF",
  },
  interactivePillText: {
    color: "#7244bb",
  },
  menuButton: {
    width: 32,
    height: 32,
    borderRadius: radii.full,
    backgroundColor: colors.white,
    alignItems: "center",
    justifyContent: "center",
    ...shadowStyles.xs,
  },
  menuContent: {
    backgroundColor: colors.white,
    borderRadius: radii.md,
  },
  detailsContainer: {
    padding: spacing.sm,
    gap: spacing.xs,
    flex: 1,
    justifyContent: "space-between",
  },
  detailsContent: {
    gap: spacing.xs,
    flex: 1,
  },
  title: {
    color: colors.grey[900],
  },
  courseName: {
    color: colors.grey[500],
  },
  subtitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  clockIcon: {
    flexShrink: 0,
  },
  subtitleText: {
    color: colors.grey[400],
  },
  learningGoalsContainer: {
    marginTop: spacing.xs,
    gap: spacing.xs,
    minHeight: 140, // Ensures consistent height even with fewer objectives
  },
  learningGoalsHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  learningGoalsTitle: {
    color: colors.grey[500],
    letterSpacing: 0.5,
  },
  progressCount: {
    color: colors.grey[600],
  },
  objectiveRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.xs,
  },
  objectiveIcon: {
    marginTop: 2,
    flexShrink: 0,
  },
  objectiveText: {
    flex: 1,
    color: colors.grey[700],
  },
  completedObjectiveText: {
    textDecorationLine: "line-through",
    color: colors.grey[400],
  },
  moreText: {
    color: colors.grey[500],
    marginLeft: spacing.lg,
  },
  progressSection: {
    marginTop: spacing.xs,
    gap: 6,
  },
  progressLabelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  progressLabel: {
    color: colors.grey[500],
  },
  progressTrack: {
    height: 8,
    borderRadius: radii.full,
    backgroundColor: colors.grey[200],
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: radii.full,
    backgroundColor: "#8e55ea",
  },
  completedRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  completedLabel: {
    color: "#3b7c0f",
  },
  actionButton: {
    marginTop: spacing.xs,
    width: "100%",
  },
});

export default CompanionILECard;